import React from "react";
import { Toaster } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import { UserProvider } from "@/hooks/use-user";
import { LoadingProvider } from "@/contexts/loading-context";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { useLoadingContext } from "@/contexts/loading-context";

import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Create from "./pages/Create";
import CreatePin from "./pages/CreatePin";
import Profile from "./pages/Profile";
import PinDetails from "./pages/PinDetails";
import Settings from "./pages/Settings";
import Collections from "./pages/Collections";
import CollectionDetails from "./pages/CollectionDetails";
import Checkout from "./pages/Checkout";
import Shop from "./pages/Shop";
import MyPins from "./pages/MyPins";
import MyStore from "./pages/MyStore";
import Commissions from "./pages/Commissions";
import CommissionCreate from "./pages/CommissionCreate";
import CommissionDetails from "./pages/CommissionDetails";
import MyCommissionRequests from "./pages/MyCommissionRequests";
import CommissionRequestDetails from "./pages/CommissionRequestDetails";
import Collabs from "./pages/Collabs";
import CampaignsList from "./pages/CampaignsList";
import CampaignDetails from "./pages/CampaignDetails";
import CreateCampaign from "./pages/CreateCampaign";
import MyWebtoons from "./pages/MyWebtoons";
import CreateWebtoon from "./pages/CreateWebtoon";
import WebtoonEpisodeReader from "./pages/WebtoonEpisodeReader";
import WebtoonDetails from "./pages/WebtoonDetails";
import MyWebnovels from "./pages/MyWebnovels";
import CreateWebnovel from "./pages/CreateWebnovel";
import WebNovelReader from "./pages/WebNovelReader";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

// Component that uses loading context
const AppContent: React.FC = () => {
  const { isLoading } = useLoadingContext();

  return (
    <>
      <LoadingScreen isLoading={isLoading} minDuration={500} />
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/create" element={<Create />} />
        <Route path="/create/pin" element={<CreatePin />} />
        <Route path="/create/webtoon" element={<CreateWebtoon />} />
        <Route path="/create/webnovel" element={<CreateWebnovel />} />
        <Route path="/profile/:username" element={<Profile />} />
        <Route path="/pin/:id" element={<PinDetails />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/collections" element={<Collections />} />
        <Route path="/collections/:id" element={<CollectionDetails />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/my-pins" element={<MyPins />} />
        <Route path="/my-store" element={<MyStore />} />
        <Route path="/commissions" element={<Commissions />} />
        <Route path="/commissions/create" element={<CommissionCreate />} />
        <Route path="/commissions/:id" element={<CommissionDetails />} />
        <Route path="/commissions/requests" element={<MyCommissionRequests />} />
        <Route path="/commissions/requests/:id" element={<CommissionRequestDetails />} />
        <Route path="/collabs" element={<Collabs />} />
        <Route path="/campaigns" element={<CampaignsList />} />
        <Route path="/campaigns/:id" element={<CampaignDetails />} />
        <Route path="/campaigns/create" element={<CreateCampaign />} />
        <Route path="/my-webtoons" element={<MyWebtoons />} />
        <Route path="/webtoon/:id" element={<WebtoonDetails />} />
        <Route path="/webtoon/:id/episode/:episodeId" element={<WebtoonEpisodeReader />} />
        <Route path="/my-webnovels" element={<MyWebnovels />} />
        <Route path="/webnovel/:id/chapter/:chapterNumber" element={<WebNovelReader />} />
      </Routes>
    </>
  );
};

// Error boundary component
class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("App error boundary caught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center p-4">
            <h2 className="text-xl font-bold mb-4">Algo deu errado</h2>
            <p className="mb-4">Desculpe, ocorreu um erro na aplicação.</p>
            <button 
              onClick={() => {
                this.setState({ hasError: false });
                window.location.reload();
              }}
              className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
            >
              Recarregar
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const App: React.FC = () => {
  return (
    <AppErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
            <UserProvider>
              <BrowserRouter>
                <LoadingProvider minDuration={500} timeout={5000}>
                  <AppContent />
                </LoadingProvider>
              </BrowserRouter>
              <Toaster />
            </UserProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </AppErrorBoundary>
  );
};

export default App;
